import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Trans } from '@lingui/macro';
import imageList, { images26 } from '../imageList';

// Fisher–Yates shuffle for random order on each load
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// Derive the photographer credit from the photo filename.
// e.g. "LapriZ-AMF-26-220.jpg" -> "LapriZ", "LAG_image-AMF-26-130.jpg" -> "L-A.G image"
const getPhotographer = (name) => {
  if (name.startsWith('LAPRIZ') || name.startsWith('LapriZ') || name.toLowerCase().startsWith('lapriz')) {
    return 'LapriZ';
  }
  if (name.startsWith('LAG_image') || name.toLowerCase().startsWith('lag_image')) {
    return 'L-A.G image';
  }
  return '';
};

const getPhotographerUrl = (photographer) => {
  if (photographer === 'LapriZ') return 'https://www.facebook.com/Lapriz';
  if (photographer === 'L-A.G image') return 'https://www.instagram.com/lag_image';
  return '';
};

const toImagePaths = (names, year) =>
  names.map((name) => ({
    name,
    fullSize: `/images/${year}/${name}`,
    thumbnail: `/images/${year}/thumbnails/${name}`,
    photographer: getPhotographer(name)
  }));

const SLIDES = [
  {
    key: '2026',
    year: '2026',
    names: images26,
    hasVideo: false,
    videoLabel: 'AFTERMOVIE - AMF 2026',
    videoSrc: '/videos/aftermovie-amf26.mp4',
    videoPoster: '/images/aftermovie-amf26-thumbnail.jpg'
  },
  {
    key: '2025',
    year: '2025',
    names: imageList,
    hasVideo: true,
    videoLabel: 'AFTERMOVIE - AMF 2025',
    videoSrc: '/videos/aftermovie-amf25.mp4',
    videoPoster: '/images/aftermovie-amf25-thumbnail.jpg'
  }
];

function Media() {
  const [slides, setSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0); // 2026 first, 2025 after
  const [visibleCount, setVisibleCount] = useState({});
  const [lightbox, setLightbox] = useState(null); // { slide, image }
  const [fullLoaded, setFullLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sliderRef = useRef(null);
  const slideRefs = useRef([]);
  const [sectionHeight, setSectionHeight] = useState(0);

  // Keep the section height synced to the active slide so each year keeps
  // its own natural size (expanding one year resets when you slide away).
  // A ResizeObserver handles content that loads after first paint (lazy
  // images, video metadata) without cutting the slide off.
  useLayoutEffect(() => {
    const slider = sliderRef.current;
    const el = slideRefs.current[activeSlide];
    if (!slider || !el) return;
    setSectionHeight(el.offsetHeight);
    const ro = new ResizeObserver(() => {
      setSectionHeight(el.offsetHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeSlide, slides]);

  useEffect(() => {
    try {
      const loaded = SLIDES.map((slide) => {
        const paths = toImagePaths(slide.names, slide.year);
        return { ...slide, images: shuffleArray(paths) };
      });
      setSlides(loaded);
      setVisibleCount(Object.fromEntries(loaded.map((s) => [s.year, 8])));
      setLoading(false);
    } catch (err) {
      setError('Failed to load images');
      setLoading(false);
      console.error('Error loading images:', err);
    }
  }, []);

  const openLightbox = (slideIndex, imageIndex) => {
    setFullLoaded(false);
    setLightbox({ slide: slideIndex, image: imageIndex });
  };

  const closeLightbox = () => {
    setLightbox(null);
  };

  const goToLightbox = (dir) => {
    if (!lightbox) return;
    setLightbox(({ slide, image }) => {
      const slideData = slides[slide];
      const next =
        image + dir < 0
          ? slideData.images.length - 1
          : (image + dir) % slideData.images.length;
      setFullLoaded(false);
      return { slide, image: next };
    });
  };

  const loadMore = (slideIndex) => {
    const year = slides[slideIndex].year;
    setVisibleCount((prev) => ({
      ...prev,
      [year]: Math.min((prev[year] || 8) + 25, slides[slideIndex].images.length)
    }));
  };

  const goToSlide = (index) => {
    if (index < 0 || index >= slides.length) return;
    setActiveSlide(index);
  };

  // Touch swipe support
  const touchStartX = React.useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 50;
    if (Math.abs(delta) > threshold) {
      if (delta > 0) {
        goToSlide(activeSlide - 1); // swipe right -> previous year
      } else {
        goToSlide(activeSlide + 1); // swipe left -> next year
      }
    }
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <section className="section bg-light py-5" id="media">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3"><Trans>Loading photos...</Trans></p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if ((error && slides.length === 0) || slides.length === 0) {
    return (
      <section className="section py-5" id="media">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 text-center">
              <div className="alert alert-warning" role="alert">
                <h4 className="alert-heading">Image Loading Issue</h4>
                <p>{error}</p>
                <hr />
                <p className="mb-0">No images were found. Please check your image paths.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const lightboxImages = lightbox ? slides[lightbox.slide].images : [];
  const lightboxIndex = lightbox ? lightbox.image : 0;

  return (
    <section className="section py-5" id="media">
      <div className="bg-overlay" style={{ opacity: 0.95 }}></div>

      {/* Year buttons at top */}
      <div className="slider-dots">
        {slides.map((slide, index) => (
          <button
            key={slide.year}
            className={`slider-dot ${index === activeSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          >
            {slide.year}
          </button>
        ))}
      </div>

      {/* Slider track with side buttons (desktop) */}
      <div
        ref={sliderRef}
        className="media-slider"
        style={{ height: sectionHeight || undefined }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="slider-track"
          style={{ transform: `translateX(-${activeSlide * 100}%)` }}
        >
          {slides.map((slide, slideIndex) => {
            const isActive = slideIndex === activeSlide;
            const slideImages = slide.images;
            const slideVisible = slideImages.slice(0, visibleCount[slide.year] || 8);
            const slideHasMore = (visibleCount[slide.year] || 8) < slideImages.length;

            return (
              <div
                key={slide.year}
                ref={(el) => (slideRefs.current[slideIndex] = el)}
                className="year-slide"
                aria-hidden={!isActive}
              >
                <div className="container">
                  {/* Video Section */}
                  {slide.hasVideo && (
                    <>
                      <div className="row justify-content-center">
                        <div className="col-12 mb-4 pb-2">
                          <div className="section-title text-center">
                            <h2 className="text-white">{slide.videoLabel}</h2>
                          </div>
                        </div>
                      </div>
                      <div className="row justify-content-center mb-5 pb-4">
                        <div className="col-12 col-md-10 col-lg-8">
                          <div className="video-card">
                            <video
                              controls
                              playsInline
                              preload="metadata"
                              poster={slide.videoPoster}
                              src={slide.videoSrc}
                            >
                              <Trans>Your browser does not support the video tag.</Trans>
                            </video>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Photo Section */}
                  <div className="row justify-content-center">
                    <div className="col-12 mb-4 pb-2">
                      <div className="section-title text-center">
                        <h2 className="text-white">PHOTOS - AMF {slide.year}</h2>
                        {slide.year === '2026' && (
                          <p className="photographer-credit">
                            <Trans>By</Trans>{' '}
                            <a
                              className="photographer-name"
                              href="https://www.facebook.com/Lapriz"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              LapriZ
                            </a>{' '}
                            <Trans>and</Trans>{' '}
                            <a
                              className="photographer-name"
                              href="https://www.instagram.com/lag_image"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              L-A.G image
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Photo Gallery */}
                  {slideImages.length > 0 ? (
                    <div className="row">
                      {slideVisible.map((image, index) => (
                        <div key={index} className="col-6 col-md-4 col-lg-3 mb-4">
                          <div
                            className="photo-thumbnail"
                            onClick={() => openLightbox(slideIndex, index)}
                          >
                            <img
                              src={image.thumbnail}
                              alt={`AMF ${slide.year} ${index + 1}`}
                              className="img-fluid"
                              loading="lazy"
                            />
                            <div className="overlay">
                              <span className="view-icon">⌞ ⌝</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="row justify-content-center">
                      <div className="col-12 text-center">
                        <p className="text-white"><Trans>No images found.</Trans></p>
                      </div>
                    </div>
                  )}

                  {/* Load More Button */}
                  {slideHasMore && (
                    <div className="row justify-content-center mt-4">
                      <div className="col-auto">
                        <button className="home-btn" onClick={() => loadMore(slideIndex)}>
                          <Trans>Load More</Trans> ({visibleCount[slide.year] || 8} / {slideImages.length})
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop side buttons (hidden on mobile — swipe instead) */}
        <button
          className={`slider-nav slider-side-btn slider-prev ${activeSlide === 0 ? 'disabled' : ''}`}
          onClick={() => goToSlide(activeSlide - 1)}
          aria-label="Previous year"
          disabled={activeSlide === 0}
        >
          &#10094;
        </button>
        <button
          className={`slider-nav slider-side-btn slider-next ${activeSlide === slides.length - 1 ? 'disabled' : ''}`}
          onClick={() => goToSlide(activeSlide + 1)}
          aria-label="Next year"
          disabled={activeSlide === slides.length - 1}
        >
          &#10095;
        </button>
      </div>

      {/* Lightbox Modal */}
      {lightbox && lightboxImages.length > 0 && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="lightbox-nav lightbox-prev" onClick={() => goToLightbox(-1)}>
              &#10094;
            </button>
            <button className="lightbox-nav lightbox-next" onClick={() => goToLightbox(1)}>
              &#10095;
            </button>

            <div className="lightbox-image-container">
              <img
                src={lightboxImages[lightboxIndex].thumbnail}
                alt="Preview"
                className={`lightbox-image preview ${fullLoaded ? 'hidden' : ''}`}
              />
              <img
                src={lightboxImages[lightboxIndex].fullSize}
                alt={`Enlarged view ${lightboxIndex + 1}`}
                className={`lightbox-image full ${fullLoaded ? 'loaded' : ''}`}
                onLoad={() => setFullLoaded(true)}
              />
              <div className="lightbox-tools">
                <a
                  className="lightbox-tool lightbox-download"
                  href={lightboxImages[lightboxIndex].fullSize}
                  download={lightboxImages[lightboxIndex].name}
                  aria-label="Download full quality photo"
                  title="Download full quality"
                >
                  &#11015;
                </a>
                <button className="lightbox-tool lightbox-close" onClick={closeLightbox}>
                  &times;
                </button>
              </div>
              <div className="lightbox-counter">
                {lightboxIndex + 1} / {lightboxImages.length}
              </div>
              {lightboxImages[lightboxIndex].photographer && (
                <div
                  className="lightbox-credit"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="credit-icon">&#128247;</span>
                  {getPhotographerUrl(lightboxImages[lightboxIndex].photographer) ? (
                    <a
                      className="credit-link"
                      href={getPhotographerUrl(lightboxImages[lightboxIndex].photographer)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {lightboxImages[lightboxIndex].photographer}
                    </a>
                  ) : (
                    <span>{lightboxImages[lightboxIndex].photographer}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .section {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          position: relative;
        }
        .bg-overlay {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          z-index: -1;
        }

        /* Full-page slider */
        .media-slider {
          position: relative;
          overflow: hidden;
          transition: height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .slider-track {
          display: flex;
          align-items: flex-start;
          transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .year-slide {
          flex: 0 0 100%;
          min-width: 0;
        }

        .slider-side-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          width: 48px;
          height: 48px;
          font-size: 22px;
        }
        .slider-side-btn.slider-prev {
          left: 12px;
        }
        .slider-side-btn.slider-next {
          right: 12px;
        }
        .slider-nav {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }
        .slider-nav:hover:not(.disabled) {
          background: rgba(67, 97, 238, 0.6);
          border-color: rgba(67, 97, 238, 0.8);
          transform: translateY(-50%) scale(1.1);
        }
        .slider-nav.disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        .slider-dots {
          display: flex;
          justify-content: center;
          gap: 12px;
          margin-bottom: 32px;
          margin-top: 8px;
        }
        .slider-dot {
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          font-size: 16px;
          font-weight: 700;
          padding: 10px 28px;
          border-radius: 30px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .slider-dot:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .slider-dot.active {
          background: linear-gradient(135deg, #4361ee 0%, #3a0ca3 100%);
          border-color: #4361ee;
          box-shadow: 0 6px 20px rgba(67, 97, 238, 0.4);
        }

        /* Mobile: hide side buttons, rely on swipe */
        @media (max-width: 991px) {
          .slider-side-btn {
            display: none;
          }
        }

        .video-card {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.12);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .video-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
        }
        .video-card video {
          width: 100%;
          display: block;
        }
        .photo-thumbnail {
          position: relative;
          cursor: pointer;
          height: 200px;
          border-radius: 20px;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.18);
          box-shadow:
            0 8px 32px rgba(0,0,0,0.3),
            inset 0 0 20px rgba(255,255,255,0.1),
            inset 0 0 60px rgba(255,255,255,0.05);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .photo-thumbnail:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow:
            0 20px 50px rgba(0,0,0,0.4),
            inset 0 0 30px rgba(255,255,255,0.15),
            0 0 40px rgba(67, 97, 238, 0.3);
          border-color: rgba(67, 97, 238, 0.5);
        }
        .photo-thumbnail::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(
            120deg,
            transparent 0%,
            rgba(255,255,255,0.15) 30%,
            transparent 60%,
            rgba(255,255,255,0.1) 100%
          );
          z-index: 2;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.4s ease;
        }
        .photo-thumbnail:hover::before {
          opacity: 1;
          animation: liquidShine 2s infinite linear;
        }
        @keyframes liquidShine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .photo-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease, filter 0.4s ease;
          filter: brightness(0.95) saturate(1.05);
        }
        .photo-thumbnail:hover img {
          transform: scale(1.08);
          filter: brightness(1.05) saturate(1.1);
        }
        .overlay {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(
            180deg,
            rgba(255,255,255,0.15) 0%,
            rgba(255,255,255,0.05) 40%,
            rgba(0,0,0,0.2) 100%
          );
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.4s ease;
          z-index: 3;
        }
        .photo-thumbnail:hover .overlay {
          opacity: 1;
          backdrop-filter: blur(3px);
        }
        .view-icon { font-size: 2rem; color: white; }

        .photographer-credit {
          margin-top: 10px;
          margin-bottom: 0;
          color: rgba(255, 255, 255, 0.75);
          font-size: 15px;
          letter-spacing: 0.5px;
        }
        .photographer-credit .photographer-name {
          color: #ffffff;
          font-weight: 600;
          text-decoration: none;
          border-bottom: 1px solid rgba(255, 255, 255, 0.4);
          transition: color 0.3s ease, border-color 0.3s ease;
        }
        .photographer-credit .photographer-name:hover {
          color: #a5b4fc;
          border-color: #a5b4fc;
        }

        .lightbox-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 50%, rgba(0,0,0,0.2) 100%);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .lightbox-content {
          position: relative;
          max-width: 90%;
          max-height: 90%;
        }
        .lightbox-tools {
          position: absolute;
          top: 12px; right: 12px;
          display: flex;
          gap: 10px;
          z-index: 1001;
        }
        .lightbox-tool {
          background: rgba(0, 0, 0, 0.55);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white; font-size: 22px;
          width: 42px; height: 42px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%;
          cursor: pointer; text-decoration: none;
          transition: background 0.3s ease, transform 0.3s ease, opacity 0.3s ease;
        }
        .lightbox-tool:hover {
          background: rgba(67, 97, 238, 0.85);
          transform: scale(1.08);
        }
        .lightbox-close { font-weight: 700; }

        /* Tools revealed on hover (fade in) over the photo */
        .lightbox-tools {
          opacity: 0;
        }
        .lightbox-image-container:hover .lightbox-tools,
        .lightbox-tools:hover,
        .lightbox-tools:focus-within {
          opacity: 1;
        }
        @media (hover: none) {
          .lightbox-tools {
            opacity: 1;
          }
        }
        .lightbox-nav {
          position: absolute; top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.1);
          border: none; color: white; font-size: 24px;
          padding: 16px; cursor: pointer; z-index: 1001;
          transition: background 0.3s ease;
        }
        .lightbox-nav:hover { background: rgba(255, 255, 255, 0.2); }
        .lightbox-prev { left: -60px; border-radius: 4px 0 0 4px; }
        .lightbox-next { right: -60px; border-radius: 0 4px 4px 0; }

        .lightbox-image-container {
          position: relative;
          display: grid;
          place-items: center;
          max-height: 80vh;
        }
        .lightbox-image-container img {
          grid-area: 1 / 1;
          max-width: 100%;
          max-height: 80vh;
          width: auto;
          height: auto;
          object-fit: contain;
        }
        .lightbox-image.preview {
          filter: blur(18px);
          transform: scale(1.03);
          transition: opacity 0.4s ease;
        }
        .lightbox-image.preview.hidden {
          opacity: 0;
        }
        .lightbox-image.full {
          opacity: 0;
          transition: opacity 0.5s ease;
        }
        .lightbox-image.full.loaded {
          opacity: 1;
        }

        .lightbox-counter {
          position: absolute;
          bottom: 10px; right: 10px;
          background: rgba(0, 0, 0, 0.6);
          color: white; padding: 5px 10px;
          border-radius: 4px; font-size: 14px;
        }

        .lightbox-credit {
          position: absolute;
          left: 0; right: 0; bottom: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 18px;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          color: white;
          font-size: 15px;
          font-weight: 600;
          letter-spacing: 0.5px;
          border-top: 1px solid rgba(255, 255, 255, 0.15);
          box-sizing: border-box;
        }
        .lightbox-credit .credit-icon {
          font-size: 16px;
          filter: grayscale(1) brightness(1.4);
        }
        .lightbox-credit .credit-link {
          color: #ffffff;
          font-weight: 600;
          text-decoration: none;
          border-bottom: 1px solid rgba(255, 255, 255, 0.4);
          transition: color 0.3s ease, border-color 0.3s ease;
        }
        .lightbox-credit .credit-link:hover {
          color: #a5b4fc;
          border-color: #a5b4fc;
        }
        @media (hover: none) {
          .lightbox-credit {
            font-size: 13px;
            padding: 10px 14px;
          }
        }

        @media (max-width: 768px) {
          .lightbox-prev { left: 10px; }
          .lightbox-next { right: 10px; }
          .slider-dot { padding: 8px 18px; font-size: 14px; }
        }
      `}</style>
    </section>
  );
}

export default Media;
